/**
 * Mirrored from `cosmo-bud-portal-backend/src/modules/bookings/dto/booking.schemas.ts`
 * and `src/modules/packs/dto/pack.schemas.ts`.
 * Drift is enforced by `scripts/diff-schemas.mjs` in CI.
 */
import { z } from 'zod';

export const BOOKING_TYPES = ['demo', 'paid'] as const;
export type BookingType = (typeof BOOKING_TYPES)[number];

export const BOOKING_STATUSES = [
  'pending',
  'accepted',
  'declined',
  'cancelled',
  'completed',
] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

export const CreateBookingSchema = z.object({
  tutorId: z.string().min(1),
  childId: z.string().min(1),
  slotStartUtc: z.string().datetime({ offset: false }),
  slotEndUtc: z.string().datetime({ offset: false }),
  type: z.enum(BOOKING_TYPES),
  parentNotes: z.string().trim().max(500).nullable().optional(),
  tutorTimeZone: z.string().min(1),
  parentTimeZone: z.string().min(1),
  classTitle: z.string().trim().max(100).optional(),
  subject: z.string().trim().max(100).optional(),
});
export type CreateBookingInput = z.infer<typeof CreateBookingSchema>;

export const TutorSlotsQuerySchema = z.object({
  from: z.string().datetime({ offset: false }),
  to: z.string().datetime({ offset: false }),
});
export type TutorSlotsQueryInput = z.infer<typeof TutorSlotsQuerySchema>;

export const TutorSlotSchema = z.object({
  startUtc: z.string(),
  endUtc: z.string(),
});
export type TutorSlot = z.infer<typeof TutorSlotSchema>;

export const RecurrenceRuleSchema = z.object({
  frequency: z.enum(['daily', 'weekly', 'biweekly', 'monthly', 'custom']),
  days: z.array(z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'])).optional(),
  until: z.string().optional(),
  count: z.number().int().min(1).max(52).optional(),
  seriesIndex: z.number().int(),
});
export type RecurrenceRule = z.infer<typeof RecurrenceRuleSchema>;

export const BookingResponseSchema = z.object({
  id: z.string(),
  tutorId: z.string(),
  parentId: z.string(),
  childId: z.string(),
  slotStartUtc: z.string(),
  slotEndUtc: z.string(),
  type: z.enum(BOOKING_TYPES),
  status: z.enum(BOOKING_STATUSES),
  videoLink: z.string().nullable(),
  tutorNotes: z.string().nullable(),
  parentNotes: z.string().nullable(),
  tutorTimeZone: z.string().optional(),
  parentTimeZone: z.string().optional(),
  seriesId: z.string().nullable().optional(),
  recurrenceRule: RecurrenceRuleSchema.nullable().optional(),
  classTitle: z.string().nullable().optional(),
  subject: z.string().nullable().optional(),
  createdAt: z.string(),
});
export type BookingResponse = z.infer<typeof BookingResponseSchema>;

export const CreateRecurringSeriesSchema = z.object({
  tutorId: z.string().min(1),
  childId: z.string().min(1),
  slotStartUtc: z.string().datetime({ offset: false }),
  slotEndUtc: z.string().datetime({ offset: false }),
  type: z.enum(BOOKING_TYPES),
  parentNotes: z.string().trim().max(500).nullable().optional(),
  tutorTimeZone: z.string().min(1),
  parentTimeZone: z.string().min(1),
  recurrenceRule: RecurrenceRuleSchema,
  classTitle: z.string().trim().max(100).optional(),
  subject: z.string().trim().max(100).optional(),
});
export type CreateRecurringSeriesInput = z.infer<typeof CreateRecurringSeriesSchema>;

export const DISPUTE_STATUSES = [
  'open',
  'resolved_refund',
  'resolved_no_refund',
] as const;
export type DisputeStatus = (typeof DISPUTE_STATUSES)[number];

export const AcceptBookingSchema = z.object({
  videoLink: z
    .string()
    .trim()
    .url({ message: 'videoLink must be a valid URL' }),
});
export type AcceptBookingInput = z.infer<typeof AcceptBookingSchema>;

export const DeclineBookingSchema = z.object({
  tutorNotes: z.string().trim().max(500).nullable().optional(),
});
export type DeclineBookingInput = z.infer<typeof DeclineBookingSchema>;

export const ConsentBookingSchema = z.object({});
export type ConsentBookingInput = z.infer<typeof ConsentBookingSchema>;

export const FileDisputeSchema = z.object({
  bookingId: z.string().min(1),
  reason: z.string().trim().min(1).max(1000),
});
export type FileDisputeInput = z.infer<typeof FileDisputeSchema>;

export const ResolveDisputeSchema = z.object({
  resolution: z.enum(['refund', 'no_refund']),
  adminNotes: z.string().trim().max(1000).nullable().optional(),
});
export type ResolveDisputeInput = z.infer<typeof ResolveDisputeSchema>;

export const DisputeResponseSchema = z.object({
  id: z.string(),
  bookingId: z.string(),
  parentId: z.string(),
  tutorId: z.string(),
  reason: z.string(),
  status: z.enum(DISPUTE_STATUSES),
  adminNotes: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type DisputeResponse = z.infer<typeof DisputeResponseSchema>;

export const PurchasePackSchema = z.object({
  tutorId: z.string().min(1),
  packSize: z.union([z.literal(8), z.literal(20), z.literal(30)]),
});
export type PurchasePackInput = z.infer<typeof PurchasePackSchema>;

export const PackPurchaseResponseSchema = z.object({
  id: z.string(),
  parentId: z.string(),
  tutorId: z.string(),
  packSize: z.union([z.literal(8), z.literal(20), z.literal(30)]),
  creditsTotal: z.number().int(),
  creditsUsed: z.number().int(),
  creditsRemaining: z.number().int(),
  paymentRef: z.string(),
  purchasedAt: z.string(),
});
export type PackPurchaseResponse = z.infer<typeof PackPurchaseResponseSchema>;

export const PackBalanceResponseSchema = z.object({
  tutorId: z.string(),
  creditsRemaining: z.number().int(),
  purchases: z.array(PackPurchaseResponseSchema),
});
export type PackBalanceResponse = z.infer<typeof PackBalanceResponseSchema>;
